import {
  CalendarDays,
  CalendarPlus,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { SESSION_TIMEOUT_MS, withTimeout } from '@/components/admin/adminError';
import { adminLogout, adminSession } from '@/lib/adminApi';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Which paths light this entry up. Nested destinations overlap — a meeting
   * detail page belongs under Meetings, but /admin/meetings/new is its own
   * entry — so prefix matching alone would highlight two rows at once.
   */
  match: (pathname: string) => boolean;
}

const NEW_MEETING = '/admin/meetings/new';

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, match: (path) => path === '/admin' },
  {
    to: '/admin/meetings',
    label: 'Meetings',
    icon: CalendarDays,
    match: (path) => path.startsWith('/admin/meetings') && path !== NEW_MEETING,
  },
  {
    to: NEW_MEETING,
    label: 'New meeting',
    icon: CalendarPlus,
    match: (path) => path === NEW_MEETING,
  },
];

type SessionState =
  | { kind: 'checking' }
  | { kind: 'signed-in'; email: string }
  | { kind: 'signed-out' };

/**
 * Shell and gate for the whole console.
 *
 * The session is verified against the server on mount rather than trusted from
 * anything in the browser: the cookie is httpOnly, so its presence is not even
 * observable here, and a stale client-side "logged in" flag would render an
 * empty console full of failing requests.
 */
export function AdminLayout() {
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionState>({ kind: 'checking' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let ignore = false;

    withTimeout(adminSession(), SESSION_TIMEOUT_MS, 'The server did not respond.')
      .then((result) => {
        if (!ignore) setSession({ kind: 'signed-in', email: result.email });
      })
      .catch(() => {
        if (!ignore) setSession({ kind: 'signed-out' });
      });

    return () => {
      ignore = true;
    };
  }, []);

  const signOut = async () => {
    setSigningOut(true);
    try {
      await adminLogout();
    } catch {
      // The session may already be gone server-side; either way the user asked
      // to leave, so send them to the login page instead of showing an error.
    }
    navigate('/admin/login', { replace: true });
  };

  if (session.kind === 'checking') {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <div className="text-muted-foreground flex flex-col items-center gap-4">
          <Spinner className="size-6" />
          <p className="text-sm">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (session.kind === 'signed-out') {
    return <Navigate to="/admin/login" replace />;
  }

  const { email } = session;

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <aside className="glass border-border/70 sticky top-0 z-30 hidden h-screen w-64 shrink-0 flex-col border-r lg:flex">
        <div className="p-5">
          <Link to="/admin" className="inline-flex rounded-lg">
            <BrandMark size="sm" />
          </Link>
          <p className="text-muted-foreground mt-3 text-[11px] font-semibold tracking-[0.09em] uppercase">
            Admin console
          </p>
        </div>

        <Separator />

        <AdminNav className="p-3" />

        <div className="border-border/70 mt-auto border-t p-4">
          <SessionCard email={email} signingOut={signingOut} onSignOut={signOut} />
        </div>
      </aside>

      <header className="glass border-border/70 sticky top-0 z-30 flex items-center justify-between gap-3 border-b px-4 py-3 lg:hidden">
        <Link to="/admin" className="inline-flex rounded-lg">
          <BrandMark size="sm" />
        </Link>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Open console menu"
          onClick={() => setDrawerOpen(true)}
        >
          <Menu className="size-4" />
        </Button>
      </header>

      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent
          showCloseButton={false}
          className="top-0 left-0 flex h-full w-72 max-w-[85vw] translate-x-0 translate-y-0 flex-col gap-0 rounded-none rounded-r-2xl border-y-0 border-l-0 p-0"
        >
          <div className="flex items-center justify-between gap-2 p-4">
            <DialogTitle className="text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
              Admin console
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close console menu"
              onClick={() => setDrawerOpen(false)}
            >
              <X className="size-4" />
            </Button>
          </div>

          <Separator />

          <AdminNav className="p-3" onNavigate={() => setDrawerOpen(false)} />

          <div className="border-border/70 mt-auto border-t p-4">
            <SessionCard email={email} signingOut={signingOut} onSignOut={signOut} />
          </div>
        </DialogContent>
      </Dialog>

      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}

function AdminNav({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  const { pathname } = useLocation();

  return (
    <nav className={cn('flex flex-col gap-1', className)} aria-label="Console sections">
      {NAV_ITEMS.map(({ to, label, icon: Icon, match }) => {
        const active = match(pathname);
        return (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
              active
                ? 'bg-primary/15 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.06]',
            )}
          >
            <Icon className="size-4 shrink-0" aria-hidden />
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

function SessionCard({
  email,
  signingOut,
  onSignOut,
}: {
  email: string;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="min-w-0">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
          Signed in as
        </p>
        <p className="mt-1 truncate text-sm font-medium" title={email}>
          {email}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={onSignOut}
        disabled={signingOut}
      >
        {signingOut ? <Spinner className="size-4" /> : <LogOut className="size-4" />}
        Sign out
      </Button>
    </div>
  );
}
